/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { FC, useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import { STUDENT_AVATAR_PATH_S3 } from 'constants/images'
import {
  findAllTestimonial,
  findAllTestimonial_findAllTestimonial_items,
  findAllTestimonialVariables,
  findAllTestimonial_findAllTestimonial_items_textFeedback as TextFeedback,
} from '__generatedTypes__/findAllTestimonial'
import {
  CMSBoosterPages,
  CreateTextFeedbackInput,
  TestimonialWhere,
  UpdateTextFeedbackInput,
} from '__generatedTypes__/globalTypes'
import { removeTextFeedback, removeTextFeedbackVariables } from '__generatedTypes__/removeTextFeedback'
import { ReactComponent as DnDIcon } from 'assets/icons/dnd.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import AvatarInput from 'components/AvatarInput'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import client from 'configs/apolloClient'
import { FIND_ALL_TESTIMONIALS } from 'queries/testimonials'
import { DELETE_TEXT_FEEDBACK } from 'queries/textFeedback'
import { getFileNameFromImagekitLink } from 'utils/getFileNameFromImagekitLink'
import { useImageUpload } from 'utils/saveImageS3'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { toast } from 'utils/toast'
import { InputField } from 'components/Input'
import { validateRequired } from 'utils/validation'
import { EMPTY_CARD_FILTERED_CLASS_NAME } from '../consts'
import * as classes from './styles'

interface IProps {
  textFeedbackInfo?: TextFeedback
  testimonial: findAllTestimonial_findAllTestimonial_items
  textFeedbackId: string
  platformQuery: Partial<TestimonialWhere>
  lastItemOrder: number
  onRemoveEmptyCard?: (removeCardId: string) => void
  onCreateFeedback?: (testimonialProps: CreateTextFeedbackInput, removeCardId: string) => void
  onUpdateFeedback?: (testimonialProps: UpdateTextFeedbackInput) => void
  selectedPageId: CMSBoosterPages
}

const TextFeedbackForm: FC<IProps> = ({
  textFeedbackInfo,
  textFeedbackId,
  platformQuery,
  lastItemOrder,
  onRemoveEmptyCard,
  testimonial,
  onUpdateFeedback,
  onCreateFeedback,
  selectedPageId,
}) => {
  const [isEditMode, setEditMode] = useState<boolean>(!textFeedbackInfo)
  const imageUpload = useImageUpload()
  const [imageError, setImageError] = useState<boolean>(false)
  const [selectedImage, setSelectedImage] = useState<File | null>()
  const [initImage, setInitImage] = useState<File | null>()
  const [loading, setLoading] = useState<boolean>(false)
  const [removeTextFeedback, { loading: removeLoading }] = useMutation<removeTextFeedback, removeTextFeedbackVariables>(
    DELETE_TEXT_FEEDBACK
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = (fieldValues: Record<string, any>) => {
    if (selectedImage) {
      setImageError(false)
      setLoading(true)

      imageUpload(selectedImage, STUDENT_AVATAR_PATH_S3)
        .then((studentAvatarUrl) => {
          const testimonialProps = {
            fullFeedback: fieldValues.fullFeedback,
            shortFeedback: fieldValues.shortFeedback,
            student: {
              avatar: studentAvatarUrl ?? fieldValues.student.avatar,
              score: fieldValues.student.score,
              fullName: fieldValues.student.fullName,
            },
          }
          if (textFeedbackInfo) {
            onUpdateFeedback &&
              onUpdateFeedback({
                id: fieldValues.id,
                order: textFeedbackInfo?.order,
                ...testimonialProps,
              })
          } else {
            onCreateFeedback &&
              onCreateFeedback(
                {
                  isSelected: false,
                  testimonial: {
                    id: testimonial.id,
                  },
                  order: lastItemOrder + 1,
                  ...testimonialProps,
                },
                textFeedbackId
              )
          }
        })
        .then(() => {
          setLoading(false)
          setEditMode(false)
        })
    } else {
      setImageError(true)
    }
  }

  useEffect(() => {
    ;(async () => {
      const link = textFeedbackInfo?.getStudentAvatar.photo
      if (link) {
        const filename = getFileNameFromImagekitLink(link)
        if (link) {
          const response = await fetch(link)
          const blob = await response.blob()
          const file = new File([blob], filename)
          if (file) {
            setInitImage(file)
            setSelectedImage(file)
          }
        }
      }
    })()
  }, [textFeedbackInfo?.getStudentAvatar?.photo, textFeedbackInfo?.student.avatar])

  const onDeleteClick = async () => {
    if (!textFeedbackInfo) return

    try {
      await removeTextFeedback({ variables: { params: textFeedbackInfo.id } })

      const existingData = await client.readQuery<findAllTestimonial, findAllTestimonialVariables>({
        query: FIND_ALL_TESTIMONIALS,
        variables: {
          query: {
            where: {
              ...platformQuery,
              pageWithOrder: { page: selectedPageId },
            },
            take: TAKE_ALL_ITEMS_COUNT,
          },
        },
      })
      const textFeedbacks =
        existingData?.findAllTestimonial.items[0].textFeedback.filter((item) => item.id !== textFeedbackInfo.id) ?? []
      const newData = {
        findAllTestimonial: {
          __typename: 'TestimonialPaginationType',
          count: 1,
          items: [
            {
              __typename: existingData?.findAllTestimonial.items[0].__typename || 'TestimonialType',
              id: existingData?.findAllTestimonial.items[0].id || '',
              title: existingData?.findAllTestimonial.items[0].title || '',
              description: existingData?.findAllTestimonial.items[0].description || '',
              textFeedback: textFeedbacks,
            },
          ],
        },
      }
      client.writeQuery({
        query: FIND_ALL_TESTIMONIALS,
        variables: {
          query: {
            where: {
              ...platformQuery,
              pageWithOrder: { page: selectedPageId },
            },
            take: TAKE_ALL_ITEMS_COUNT,
          },
        },
        data: newData,
      })

      toast.success('Testimonial deleted successfully')
    } catch (error) {
      toast.error('Failed to delete testimonial')
    }
  }

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={textFeedbackInfo}
      render={({ handleSubmit, values, form: { reset } }) => (
        <form onSubmit={handleSubmit}>
          <div css={classes.container} data-testid={`${textFeedbackId}-form`}>
            <div css={classes.header} className={onRemoveEmptyCard && EMPTY_CARD_FILTERED_CLASS_NAME}>
              <Typography variant="body2" css={classes.label}>
                Student Avatar
              </Typography>
              {!onRemoveEmptyCard && <DnDIcon css={classes.dndIcon} />}
            </div>
            <AvatarInput
              imageError={imageError}
              setImageError={setImageError}
              isEditMode={isEditMode}
              selectedImage={selectedImage}
              setSelectedImage={setSelectedImage}
              id={textFeedbackId}
            />
            <InputField
              name="student.fullName"
              validate={validateRequired}
              label="Student Name"
              placeholder="Enter Student Name"
              disabled={!isEditMode}
              fullWidth
            />
            <InputField
              name="student.score"
              validate={validateRequired}
              label="Score"
              placeholder="Enter Score"
              disabled={!isEditMode}
              fullWidth
            />
            <TextareaAutosize
              value={values.shortFeedback}
              css={[classes.textareaAutosize, isEditMode && classes.textareaAutosizeActive]}
              disabled={!isEditMode}
              label="Shortened Testimonial"
              placeholder="Shortened Testimonial"
              validate={validateRequired}
              name="shortFeedback"
            />
            <TextareaAutosize
              value={values.fullFeedback}
              css={[classes.textareaAutosize, isEditMode && classes.textareaAutosizeActive]}
              disabled={!isEditMode}
              label="Full Testimonial"
              placeholder="Full Testimonial"
              validate={validateRequired}
              name="fullFeedback"
            />
            {isEditMode ? (
              <div css={classes.actionButtons}>
                {textFeedbackInfo && (
                  <Button fullWidth size="large" color="red" variant="outlined" onClick={onDeleteClick}>
                    {removeLoading ? (
                      '...Loading'
                    ) : (
                      <>
                        <TrashIcon />
                        Delete
                      </>
                    )}
                  </Button>
                )}
                <Button
                  fullWidth
                  size="large"
                  color="blue"
                  variant="outlined"
                  onClick={() => {
                    reset(textFeedbackInfo)
                    setEditMode(false)
                    setSelectedImage(initImage)
                    onRemoveEmptyCard && onRemoveEmptyCard(textFeedbackId)
                    setImageError(false)
                  }}
                >
                  Cancel
                </Button>
                <Button fullWidth size="large" color="blue" type="submit">
                  {loading ? '...Loading' : 'Confirm Edits'}
                </Button>
              </div>
            ) : (
              <Button fullWidth size="large" color="blue" onClick={() => setEditMode(true)}>
                Edit Testimonial
              </Button>
            )}
          </div>
        </form>
      )}
    />
  )
}

export default TextFeedbackForm
